/**
 * Select a user, with autocompletion from backend.
 */

import {
  SelectField,
  type SelectFieldOption,
  useDebouncedState,
} from '@wandb/components';
import * as _ from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';

import {apolloClient} from '../../apolloClient';
import {type UserEdge} from '../../generated/graphql';
import {
  FIND_USERS_BY_USERNAME_QUERY,
  FIND_USERS_QUERY,
} from '../../graphql/users';
import {type ApolloQueryResult} from '../../state/graphql/apolloWithAbort';

export type UserOption = {
  value: string;
  label: string;
  photoUrl: string | null;
  name: string | null;
  username: string | null;
  email: string | null;
  deactivatedAt: string | null;
  isDisabled: boolean;
};

const userEdgeToOption = (edge: UserEdge): UserOption => {
  const {node} = edge;
  const value = node?.id ?? '';
  const name = node?.name ?? null;
  const username = node?.username ?? null;
  const email = node?.email ?? null;
  const deactivatedAt = node?.deletedAt ?? null;
  const isDisabled = username ? deactivatedAt != null : true;
  return {
    value,
    label: name ?? value,
    photoUrl: node?.photoUrl ?? null,
    name,
    username,
    email,
    deactivatedAt,
    isDisabled,
  };
};

const responseToOptions = (result: ApolloQueryResult<any>): UserOption[] => {
  return result.data.users.edges.map(userEdgeToOption);
};

const loadOptions = async (inputValue: string): Promise<UserOption[]> => {
  // Ideally the backend users query API would be more flexible or have
  // better ranking for these purposes. For now, just do two queries,
  // one query for an exact username match (make it the top result if found)
  // and one query for a substring match on username, name, or email.
  const searchText = inputValue.trim();
  const promises = [];
  if (searchText) {
    promises.push(
      apolloClient.query({
        query: FIND_USERS_BY_USERNAME_QUERY,
        variables: {
          usernames: [searchText],
          limit: 1,
        },
      })
    );
  }
  promises.push(
    apolloClient.query({
      query: FIND_USERS_QUERY,
      variables: {
        query: searchText,
        limit: 20,
      },
    })
  );
  const results = await Promise.all(promises);
  const sorted = results
    .map(r => _.sortBy(responseToOptions(r), ['isDisabled', 'label']))
    .flat();
  return _.uniqBy(sorted, 'value');
};

type SelectUserProps = {
  onSelectUser?: (user: UserOption | null) => void;
  'aria-label'?: string;
  fullWidth?: boolean;
  placeholder?: string;
};

export const SelectUser = ({
  onSelectUser,
  'aria-label': ariaLabel,
  fullWidth = false,
  placeholder,
}: SelectUserProps) => {
  const {
    state: query,
    debouncedState: debouncedQuery,
    isPending,
    setState: setQuery,
  } = useDebouncedState('', 300);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    loadOptions(debouncedQuery)
      .then(options => {
        if (!cancelled) {
          setUsers(options);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFetching(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const items: SelectFieldOption[] = useMemo(
    () =>
      users.map(u => ({
        id: u.value,
        label: u.name ?? u.username ?? u.value,
        description: [
          u.username && u.name ? u.username : null,
          u.email,
          !u.username && '(incomplete signup)',
          u.deactivatedAt && '(deactivated)',
        ]
          .filter(Boolean)
          .join(' · '),
        decorationLeft: u.photoUrl
          ? {
              avatarProps: {
                src: u.photoUrl,
                alt: u.name ?? u.username ?? '',
              },
            }
          : undefined,
      })),
    [users]
  );

  return (
    <SelectField
      allowsEmptyCollection
      aria-label={ariaLabel ?? 'Select user'}
      fullWidth={fullWidth}
      isLoading={isPending || fetching}
      items={items}
      disabledKeys={users.filter(u => u.isDisabled).map(u => u.value)}
      placeholder={placeholder ?? 'Select user...'}
      search={{
        value: query,
        onInput: setQuery,
        placeholder: 'Search by name, username, or email',
      }}
      onChange={key => onSelectUser?.(users.find(u => u.value === key) ?? null)}
    />
  );
};
